/**
 * ContextMenu.tsx - ContextMenu - Universal context menu component for all views
 * 
 * CHANGE HISTORY:
 * - 251101_0847 [pending] danielbeer: Added TypeScript interfaces
 * - 251021_0421 [7529d5e] danielbeer: feat: migrate all packages to TypeScript structure
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import './ContextMenu.css';

export interface MenuItem {
  id?: string;
  label?: string;
  icon?: string;
  shortcut?: string;
  action?: () => void;
  submenu?: MenuItem[];
  separator?: boolean;
  disabled?: boolean;
  danger?: boolean;
}

export interface ContextMenuProps {
  isOpen: boolean;
  position: { x: number; y: number } | null;
  items?: MenuItem[];
  onClose?: () => void;
  onItemClick?: (item: MenuItem) => void;
}

const ContextMenu: React.FC<ContextMenuProps> = ({ 
  isOpen, 
  position, 
  items = [], 
  onClose,
  onItemClick 
}) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);
  const [typeBuffer, setTypeBuffer] = useState('');
  const typeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Filter out separators for navigation
  const navigableItems = items.filter(item => !item.separator && !item.disabled);

  // Reset state when menu opens/closes
  useEffect(() => {
    if (isOpen) {
      setSelectedIndex(0);
      setOpenSubmenu(null);
      setTypeBuffer('');
    }
  }, [isOpen]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Prevent default for navigation keys
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Enter', 'Escape'].includes(e.key)) {
        e.preventDefault();
        e.stopPropagation();
      }

      switch (e.key) {
        case 'ArrowDown':
          setSelectedIndex(prev => {
            const next = prev + 1;
            return next >= navigableItems.length ? 0 : next;
          });
          break;

        case 'ArrowUp':
          setSelectedIndex(prev => {
            const next = prev - 1;
            return next < 0 ? navigableItems.length - 1 : next;
          });
          break;

        case 'ArrowRight':
          // Open submenu if current item has one
          if (navigableItems[selectedIndex]?.submenu) {
            setOpenSubmenu(navigableItems[selectedIndex].id || null);
          }
          break;

        case 'ArrowLeft':
          // Close submenu
          setOpenSubmenu(null);
          break;

        case 'Enter':
          // Execute selected item
          const selectedItem = navigableItems[selectedIndex];
          if (selectedItem && !selectedItem.disabled) {
            if (selectedItem.submenu) {
              setOpenSubmenu(selectedItem.id || null);
            } else if (selectedItem.action) {
              selectedItem.action();
              if (onItemClick) onItemClick(selectedItem);
              if (onClose) onClose();
            }
          }
          break;

        case 'Escape':
          if (openSubmenu) {
            setOpenSubmenu(null);
          } else {
            if (onClose) onClose();
          }
          break;

        default:
          // Type-to-select
          if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
            handleTypeToSelect(e.key);
          }
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [isOpen, selectedIndex, navigableItems, openSubmenu, onClose, onItemClick]);

  // Type-to-select functionality
  const handleTypeToSelect = useCallback((char: string) => {
    // Clear previous timeout
    if (typeTimeoutRef.current) {
      clearTimeout(typeTimeoutRef.current);
    }

    // Add character to buffer
    const newBuffer = typeBuffer + char.toLowerCase();
    setTypeBuffer(newBuffer);

    // Find matching item
    const matchIndex = navigableItems.findIndex(item => 
      item.label?.toLowerCase().startsWith(newBuffer)
    );

    if (matchIndex !== -1) {
      setSelectedIndex(matchIndex);
    }

    // Clear buffer after 1 second
    typeTimeoutRef.current = setTimeout(() => {
      setTypeBuffer('');
    }, 1000);
  }, [typeBuffer, navigableItems]);

  // Click outside to close (prevent propagation)
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        e.preventDefault();
        e.stopPropagation();
        if (onClose) onClose();
      }
    };

    // Use capture phase to prevent background clicks
    document.addEventListener('mousedown', handleClickOutside, true);
    document.addEventListener('contextmenu', handleClickOutside, true);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
      document.removeEventListener('contextmenu', handleClickOutside, true);
    };
  }, [isOpen, onClose]);

  // Handle item click
  const handleItemClick = useCallback((item: MenuItem, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (item.disabled) return;

    if (item.submenu) {
      setOpenSubmenu(openSubmenu === item.id ? null : (item.id || null));
    } else if (item.action) {
      item.action();
      if (onItemClick) onItemClick(item);
      if (onClose) onClose();
    }
  }, [openSubmenu, onItemClick, onClose]);

  // Handle mouse enter for selection
  const handleMouseEnter = useCallback((index: number) => {
    const navigableIndex = navigableItems.findIndex(item => item.id === items[index].id);
    if (navigableIndex !== -1) {
      setSelectedIndex(navigableIndex);
    }
  }, [navigableItems, items]);

  // Render menu items
  const renderItems = (menuItems: MenuItem[], isSubmenu = false) => {
    return menuItems.map((item: MenuItem, index: number) => {
      if (item.separator) {
        return <div key={`separator-${index}`} className="context-menu-separator" />;
      }

      const isSelected = !isSubmenu && navigableItems[selectedIndex]?.id === item.id;
      const hasSubmenu = item.submenu && item.submenu.length > 0;
      const isSubmenuOpen = openSubmenu === item.id;

      return (
        <div
          key={item.id || index}
          className={`context-menu-item ${isSelected ? 'selected' : ''} ${item.disabled ? 'disabled' : ''} ${item.danger ? 'danger' : ''}`}
          onClick={(e) => handleItemClick(item, e)}
          onMouseEnter={() => handleMouseEnter(index)}
        >
          <span className="context-menu-item-icon">{item.icon || ''}</span>
          <span className="context-menu-item-label">{item.label}</span>
          {item.shortcut && (
            <span className="context-menu-item-shortcut">{item.shortcut}</span>
          )}
          {hasSubmenu && (
            <span className="context-menu-item-arrow">▶</span>
          )}
          
          {hasSubmenu && isSubmenuOpen && item.submenu && (
            <div className="context-menu-submenu">
              {renderItems(item.submenu, true)}
            </div>
          )}
        </div>
      );
    });
  };

  if (!isOpen || !position) return null;

  // Calculate position (ensure menu stays on screen)
  const menuStyle = {
    left: position.x,
    top: position.y,
  };

  return (
    <div
      ref={menuRef}
      className="context-menu"
      style={menuStyle}
      onContextMenu={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      {renderItems(items)}
    </div>
  );
};

export default ContextMenu;
